import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import styles from './article-interaction.module.scss';
import {useObservable} from '@/hooks/use-observable.hook';
import {LayoutObservable} from '@/observables/layout.observable';
import Overlay from 'react-bootstrap/Overlay';

import CommentIcon from '@/assets/icons/article-comments.svg';
import SaveIcon from '@/assets/icons/article-save.svg';
import ShareIcon from '@/assets/icons/article-share.svg';
import LikeIcon from '@/assets/icons/article-likes.svg';
import FacebookIcon from '@/assets/icons/media-facebook.svg';
import LinkIcon from '@/assets/icons/media-link.svg';
import LinkedInIcon from '@/assets/icons/media-linkedin.svg';
import TwitterIcon from '@/assets/icons/media-twitter.svg';
import ToastChecked from '@/assets/icons/toast_checked.svg'

import { useTranslation } from '@/hooks';

const Toast = ({ message, hide }) => {
  const [toastMessage, setToastMessage] = useState(message);
  const hideTimeout = window.setTimeout(setTimeout(hide, 5000));

  useEffect(() => {
    window.clearTimeout(hideTimeout);
    setToastMessage(toastMessage);
  }, [message]);

  return (
    <div className={styles.cgArticleInteraction__toast}>
      <p className={styles.cgArticleInteraction__toastText}><ToastChecked />{toastMessage}</p>
    </div>
  )
}

const MediaMenu = ({ linkToShare, relativeUrl = true, text, target, show, onClose, setToastMessage }) => {
  const { t } = useTranslation();
  const handleShareClick = (baseUrl, linkToShare, relativeUrl, text) => {
    if (typeof window != 'undefined') {
      let url = baseUrl;
      if (relativeUrl) {
        url += window.location.host !== 'localhost:3000' ? window.location.host : 'https://www.caregiving.dev';
      }
      url += linkToShare + text;
      window.open(url, 'sharer', 'toolbar=0,status=0,width=626,height=436');
    }
  }

  const copy = () => {
    navigator.clipboard.writeText(window.location.href);
    setToastMessage(t('articlePageTranslations.copiedToClipboard'));
    onClose()
  }

  return (
    <div className={styles.cgArticleInteraction__mediaMenu}>
      <Overlay
        target={target.current}
        show={show} placement="top"
        rootClose={true}
        onHide={onClose}
        popperConfig={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [-97, 10], // X, Y offset adjustments
              },
            },
          ],
        }}
        arrowProps>
        {({
          placement: _placement,
          arrowProps: _arrowProps,
          show: _show,
          popper: _popper,
          hasDoneInitialMeasure: _hasDoneInitialMeasure,
          ...props
        }) => (
          <div
            {...props}
            style={{
              /* eslint-disable react/prop-types */
              ...props?.style,
              position: 'absolute',
              backgroundColor: '#2B2A35',
              padding: '2px 10px',
              color: 'white',
              borderRadius: '999px',
              width: '221px',
              height: '60px',
              bottom: '0',
              left: '20px'
            }}
          >
            <div className={styles.cgArticleInteraction__media}>
              <button className={styles.cgArticleInteraction__media__button} aria-label="Facebook"
                onClick={() => { handleShareClick('https://www.facebook.com/share.php?u=', linkToShare, relativeUrl, text ? `&quote=${text}` : '') }}>
                <FacebookIcon />
              </button>
              <button className={styles.cgArticleInteraction__media__button} aria-label="Link"
                onClick={copy}>
                <LinkIcon />
              </button>
              <button className={styles.cgArticleInteraction__media__button} aria-label="Linkedin"
                onClick={() => { handleShareClick('https://www.linkedin.com/shareArticle?mini=true&url=', linkToShare, relativeUrl, text ? `&title=${text}` : '') }}>
                <LinkedInIcon />
              </button>
              <button className={styles.cgArticleInteraction__media__button} aria-label="Twitter"
                onClick={() => { handleShareClick('https://twitter.com/intent/tweet?url=', linkToShare, relativeUrl, text ? `&text=${text}` : '') }}>
                <TwitterIcon />
              </button>
            </div>
          </div>
        )}
      </Overlay>
    </div>
  );
}

const ArticleInteraction = ({ article, commentCount, likesCount, isSaved, isLiked, isCommentOpen,
  onCommentsClicked, onLikesClicked, onSaveClicked, isSticky, showLogin, isLoggedIn }) => {
  const [show, setShow] = useState(false);
  const target = useRef(null);
  const shareRef = useRef(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [stickyStyle, setStickyStyle] = useState({});
  const [layoutRef] = useObservable(LayoutObservable.layoutRef$);

  const hide = () => {
    setShowToast(false);
  }

  const displayToast = (message) => {
    setToastMessage(message);
    setShowToast(true);
  }

  useEffect(() => {
    setStickyStyle(generateStickyStyle());
  }, [isSticky]);

  useEffect(() => {
    let resizeObserver;

    if (layoutRef && !resizeObserver) {
      resizeObserver = new ResizeObserver(() => {
        setStickyStyle(generateStickyStyle());
      });
      resizeObserver.observe(layoutRef.current);
    }

    // Cleanup function
    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [layoutRef]);

  const generateStickyStyle = () => {
    if (!isSticky) {
      return {}
    }
    let stickyStyle = {
      width: '100%'
    };
    if (layoutRef?.current) {
      const computedStyles = window.getComputedStyle(layoutRef.current);
      stickyStyle.width = 'auto';
      stickyStyle.marginLeft = computedStyles.paddingLeft
    }
    return stickyStyle;
  }

  return (
      <div className={`${styles.cgArticleInteraction} ${isSticky ? styles.cgArticleInteraction__sticky : ''}`}
           style={isSticky ? stickyStyle : {}}
           ref={target}>
        <div className={styles.cgArticleInteraction__dividerBottom}/>
        <div className={styles.cgArticleInteraction__interactionPill}>
          <button aria-label="Comment"
                  className={`${isCommentOpen ? styles.cgArticleInteraction__interactionPill__active : ''} ${styles.cgArticleInteraction__interactionComment}`}
                  onClick={onCommentsClicked}><CommentIcon className={styles.cgArticleInteraction__svg}/>
            <p className={styles.cgArticleInteraction__interactionText}> {commentCount} </p>
          </button>

          <button aria-label="Like"
                  className={`${styles.cgArticleInteraction__interactionLikes} ${isLiked ? styles.cgArticleInteraction__interactionPill__active : ''}`}
                  onClick={() => (isLoggedIn ? onLikesClicked(displayToast, 'liked') : showLogin())}>
            <LikeIcon className={styles.cgArticleInteraction__svg}/>
            <p className={styles.cgArticleInteraction__interactionText}> {likesCount} </p>
          </button>

          <button aria-label="Save"
                  className={`${styles.cgArticleInteraction__interactionSave} ${isSaved ? styles.cgArticleInteraction__interactionPill__active : ''}`}
                  onClick={() => (isLoggedIn ? onSaveClicked(displayToast, 'saved') : showLogin())}>
            <SaveIcon className={styles.cgArticleInteraction__svg}/>
          </button>
          <button aria-label="Share"
                  className={`${show ? styles.cgArticleInteraction__interactionPill__active : ''} ${styles.cgArticleInteraction__interactionShare}`}
                  onClick={() => setShow(!show)} ref={shareRef}><ShareIcon
              className={styles.cgArticleInteraction__svg}/></button>
          <MediaMenu linkToShare={article?.url?.url} text={article?.title} target={shareRef} show={show}
                     onClose={() => setShow(false)} setToastMessage={displayToast}/>
          {showToast && <Toast message={toastMessage} hide={hide}/>}
        </div>
      </div>
  )
}

Toast.propTypes = {
  message: PropTypes.string,
  hide: PropTypes.func
}

MediaMenu.propTypes = {
  linkToShare: PropTypes.string,
  relativeUrl: PropTypes.string,
  text: PropTypes.string,
  target: PropTypes.object,
  show: PropTypes.bool,
  onClose: PropTypes.func,
  setToastMessage: PropTypes.func,
}

ArticleInteraction.propTypes = {
  article: PropTypes.object,
  commentCount: PropTypes.number,
  likesCount: PropTypes.number,
  isLiked: PropTypes.bool,
  isSaved: PropTypes.bool,
  isPartOfCollection: PropTypes.bool,
  isSticky: PropTypes.bool,
  isLoggedIn: PropTypes.bool,
  isCommentOpen: PropTypes.bool,
  onCommentsClicked: PropTypes.func,
  onLikesClicked: PropTypes.func,
  onSaveClicked: PropTypes.func,
  showLogin: PropTypes.func,
};


export default ArticleInteraction;
